<script setup lang="ts">
    import { useIntersectionObserver } from '@vueuse/core';
    import { buildProductDetailPath, slugify, useRequiredProductSectionPath } from '#imports';

    import type { SearchHit, SearchResponse } from '@/types/sammy/response';
    import type { FilterOption } from '@/types/filters';
    import type { SelectOption } from '~/components/shared/SelectInput.vue';

    const props = defineProps<{ blok: FachkreisPLPStoryblok }>();

    const LIMIT = 12;

    const { urlSegment, lang } = useLanguage();
    const { t } = useI18n();
    const productSectionPath = useRequiredProductSectionPath();
    const runtimeConfig = useRuntimeConfig();

    const router = useRouter();
    const lazyLoadingMarker = ref<HTMLElement | null>(null);

    const {
        fetchedProducts,
        fetchProducts,
        filters,
        order,
        pendingMore,
        queryKey,
        selectedFilters,
        selectedFiltersCount,
        selectedFiltersParams,
        syncStateFromInitialData,
        totalCountKey,
        transformResponse,
        updateFilter,
        resetFilters,
        removeSelectedFilter,
        fetchMoreProducts
    } = useFachportalProductSearch({ limit: LIMIT });

    const { data: totalProductCount } = await useAsyncData(totalCountKey, () => fetchProducts(), {
        watch: [totalCountKey],
        transform: (response: SearchResponse) => response?.es_result?.hits?.total?.value || 0
    });

    const { data: initialData, pending: searchPending } = await useAsyncData(
        queryKey,
        () =>
            fetchProducts({
                from: 0,
                size: LIMIT,
                ...useRoute().query
            }),
        {
            watch: [queryKey],
            transform: transformResponse
        }
    );

    watch(
        () => initialData.value,
        (data) => {
            syncStateFromInitialData(data);
        },
        { immediate: true }
    );

    const products = computed<SearchHit[]>(() => [
        ...(initialData.value?.products ?? []),
        ...(fetchedProducts.value ?? [])
    ]);
    const totalFilteredProductCount = computed(() => initialData.value?.totalFilteredProductCount || 0);
    const pending = computed(() => searchPending.value || pendingMore.value);

    watch([order, selectedFiltersParams], ([newOrder, newSelectedFiltersParams]) => {
        router.replace({ query: { order: newOrder, ...newSelectedFiltersParams } });
    });

    useIntersectionObserver(
        lazyLoadingMarker,
        ([entry]) => {
            if (entry?.isIntersecting) {
                fetchMoreProducts(products.value.length);
            }
        },
        { rootMargin: '100px', threshold: 0 }
    );

    useHead({
        meta: [
            { name: 'description', content: props.blok.seo?.description || runtimeConfig.public.defaultTitleSuffix }
        ],
        htmlAttrs: {
            lang: lang.value
        }
    });

    usePageTitle();

    const getProductNameWithFallback = (product: SearchHit) => {
        if (product._source.censhare_name?.[lang.value]) {
            return product._source.censhare_name[lang.value];
        } else if (product._source.censhare_name?.['default']) {
            return product._source.censhare_name['default'];
        }

        if (Array.isArray(product._source.medice_product_brand)) {
            return product._source.medice_product_brand[0];
        } else if (typeof product._source.medice_product_brand === 'string') {
            return product._source.medice_product_brand;
        } else {
            console.warn('Invalid product name at ', product);
            return '__INVALID__';
        }
    };

    const getProductDetailLink = (product: SearchHit) => {
        return buildProductDetailPath(
            urlSegment.value,
            productSectionPath.value,
            slugify(getProductNameWithFallback(product))
        );
    };

    const sortingOptions = computed<SelectOption[]>(() => [
        {
            label: t('pagePlp.sort.option1'),
            value: 'asc',
            selected: true
        },
        {
            label: t('pagePlp.sort.option2'),
            value: 'desc',
            selected: true
        }
    ]);
</script>

<template>
    <SidebarGrid
        :title="props.blok.title || 'EMPTY'"
        :mood-image="props.blok.moodImage"
        :sidebar-title="t('pagePlp.sidebar.title')"
        :sidebar-button-open-text="t('pagePlp.sidebar.buttonOpenText')"
        :sidebar-button-closed-text="t('pagePlp.sidebar.buttonClosedText')"
        :filter-count="selectedFiltersCount"
        :total-found-entries="totalFilteredProductCount"
        @reset-filters="resetFilters"
    >
        <template #sidebar>
            <template
                v-for="filter in filters"
                :key="filter.type"
            >
                <component
                    :is="filter.component"
                    v-bind="filter.props"
                    @update:selected-option="($event: FilterOption) => updateFilter(filter.type, $event)"
                    @update:is-active="($event: FilterOption) => updateFilter(filter.type, $event)"
                />
            </template>
        </template>

        <template #user-actions>
            <div class="select-element">
                <SelectInput
                    id="sort-select"
                    v-model="order"
                    :options="sortingOptions"
                    :label="`${t('pagePlp.sort.sortBy')}`"
                    :aria-label="t('pagePlp.sort.option1')"
                />
            </div>
        </template>

        <template #filter-chips>
            <FilterChips
                :selected-filters="selectedFilters"
                :total-entries="totalProductCount"
                :filtered-entries="products?.length"
                @remove="removeSelectedFilter"
            />
        </template>

        <template #content>
            <ul
                v-if="products?.length > 0"
                class="product-grid"
                data-testid="fachportal-product-grid"
            >
                <li
                    v-for="product in products"
                    :key="product._id"
                    class="product"
                >
                    <FachkreisProductCard
                        :name="getProductNameWithFallback(product)"
                        :image-id="product._source.medice_main_picture_id"
                        :is-prescription-only="product._source.medice_product_prescription_only"
                        :link-to="getProductDetailLink(product)"
                    />
                </li>
            </ul>

            <div
                v-if="pending"
                class="spinner"
            >
                <LoadingSpinner />
            </div>

            <span ref="lazyLoadingMarker" />
        </template>
    </SidebarGrid>
</template>

<style scoped>
    @import '~/assets/scss/pages/utils/shared-components.scss';
    @import '~/assets/scss/fachkreis/fachkreis-plp.scss';
</style>
